<template>
  <slide-out
  style="z-index: 200;"
    dock="right"
    :size="sideOutCurrentSize"
    ref="facilitySlideout"
    @resize="OnResize"
    :min-size="sideOutMinSize"
    :allow-resize="nested ? false : true"
    :append-to="nested ? '' : '#app'"
    fixed
    :visible.sync="slideouts.active"
    v-on:close="onSlideoutClosing">
    <template v-slot:header>
      <h3 v-if="isFetching" class="font-weight-bold pa-1" small>
        <v-progress-circular :size="16" width="2" indeterminate class="mr-1"></v-progress-circular>
        Fetching Facility ...
      </h3>
      <h3 v-else-if="selected.id" class="font-weight-bold pa-1" small>
        <v-icon left>mdi-pencil-box-outline</v-icon> Update: '{{ selected.name }}'
      </h3>
      <h3 v-else class="font-weight-bold pa-1" small>
        <v-icon left>mdi-plus-box-outline</v-icon> Add Facility
      </h3>
      <v-btn @click="closeUpdateSlideout()" icon>
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </template>

    <div>
      <div class="text-center d-flex flex-column" v-if="isFetching">
        <img width="300" src="/img/loading.svg" class="py-5 my-8" style="opacity: 0.54" />
        <p class="font-weight-medium">Loading Facility...</p>
      </div>
      <v-form v-else class="pa-4" ref="updateForm" v-model="valid">
        <SingleImageUploader
          id="facility-uploader"
          apiUrl="api/Facilities/Image"
          class="mb-4"
          v-model="selected.imageUrl"></SingleImageUploader>
        <v-text-field
          dense
          outlined
          v-model="selected.name"
          :rules="[formRules.required]"
          label="Facility Name"
          placeholder="Type Facility Name"></v-text-field>
        <ClientSelector
          v-model="selected.clientId"
          label="Client"
          placeholder="Select or create a client"
          class="mb-4" />
        
        <p class="text-caption font-italic text--secondary mt-4" style="margin-top: -8px; line-height: 1.3;">
          Groups assets into a single entity when treated as separate facilities in AVA, ensuring tag information is consistently applied across all grouped assets.
        </p>
        <v-select
          dense
          outlined
          v-model="selected.facilityGroupId"
          :items="facilityGroups"
          item-text="name"
          item-value="id"
          label="Facility Grouping"
          placeholder="Select a facility group (optional)"
          clearable
          class="mb-2">
        </v-select>
                
        <v-autocomplete
          v-if="canViewUsers"
          dense
          outlined
          v-model="selected.userIds"
          :items="users"
          item-value="id"
          item-text="displayName"
          label="Assigned Users"
          placeholder="Search and assign users"
          multiple
          chips
          small-chips
          deletable-chips
          :menu-props="{ maxHeight: 350 }"
          class="mb-2">
          <template v-slot:prepend-item>
            <v-list-item ripple @mousedown.prevent @click="toggleSelectAllUsers">
              <v-list-item-action>
                <v-icon :color="selected.userIds && selected.userIds.length > 0 ? 'primary' : ''">
                  {{ selectAllUsersIcon }}
                </v-icon>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>
                  {{ selected.userIds && selected.userIds.length === users.length ? 'Deselect All' : 'Select All' }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-divider class="mt-2"></v-divider>
          </template>
          <template v-slot:selection="{ item, index }">
            <v-chip
              v-if="index < maxVisibleChips"
              small
              close
              @click:close="removeUser(item.id)">
              {{ getUserDisplayName(item) }}
            </v-chip>
            <span
              v-else-if="index === maxVisibleChips"
              class="text-caption grey--text ml-1">
              (+{{ selected.userIds.length - maxVisibleChips }} others)
            </span>
          </template>
          <template v-slot:item="{ item, on, attrs }">
            <v-list-item v-on="on" v-bind="attrs">
              <v-list-item-action>
                <v-checkbox
                  :input-value="selected.userIds && selected.userIds.includes(item.id)"
                  :ripple="false"
                  dense
                  hide-details
                  readonly></v-checkbox>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>{{ getUserDisplayName(item) }}</v-list-item-title>
                <v-list-item-subtitle>{{ item.email }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action v-if="item.roles && item.roles.length">
                <div class="d-flex flex-wrap justify-end" style="gap: 2px;">
                  <v-chip
                    v-for="role in item.roles"
                    :key="role.name"
                    x-small
                    label
                    color="deep-purple"
                    text-color="white"
                    style="font-size: 10px; font-weight: 600;">
                    {{ role.name }}
                  </v-chip>
                </div>
              </v-list-item-action>
            </v-list-item>
          </template>
        </v-autocomplete>

        <v-label class="black--text mb-2 d-block">Address</v-label>
        <v-text-field
          dense
          outlined
          v-model="selected.address.line1"
          placeholder="Company Address"></v-text-field>
        <v-row>
          <v-col>
            <v-label class="black--text mb-2 d-block">City</v-label>
            <v-text-field
              dense
              outlined
              v-model="selected.address.city"
              placeholder="City"></v-text-field>
          </v-col>
          <v-col>
            <v-label class="black--text mb-2 d-block">State</v-label>
            <v-text-field
              dense
              outlined
              v-model="selected.address.state"
              placeholder="State"></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-label class="black--text mb-2 d-block">Country</v-label>
            <v-text-field
              dense
              outlined
              v-model="selected.address.country"
              placeholder="Country"></v-text-field>
          </v-col>
        </v-row>
      </v-form>
    </div>
    <template v-slot:footer>
      <v-card-actions>
        <v-btn text color="error" @click="closeUpdateSlideout()">
          <v-icon left>mdi-chevron-left</v-icon> Close
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          :disabled="!valid || slideouts.isLoading || !slideouts.hasChanges"
          @click="updateConfirmed()"
          :loading="slideouts.isLoading">
          <v-icon left>{{ selected.id ? 'mdi-check' : 'mdi-plus' }}</v-icon>
          {{ selected.id ? 'Save Changes' : 'Create' }}
        </v-btn>
      </v-card-actions>
    </template>
  </slide-out>
</template>

<script>
  import formRules from '@Share/config/form-rules';
  import { $handleError, $isEqual, $stripQueryParams, $has } from '@/utils';
  import moment from 'dayjs';
  import SingleImageUploader from '@Share/components/SingleImageUploader.vue';
  import ClientSelector from '@/features/Client/components/ClientSelector.vue';
  import { log, warn, error } from '@/utils/debugLogger';
  import { mapGetters } from 'vuex';
  import permissions from '@Share/config/permissions';

  export default {
    name: 'edit-Facility',
    data() {
      return {
        valid: false,
        formRules,
        isFetching: false,
        selected: { address: {}, userIds: [] },
        modal: false,
        selectedCemented: { address: {}, userIds: [] },
        facilityGroups: [],
        maxVisibleChips: 3,
        slideouts: {
          active: false,
          isLoading: false,
          isClosing: false,
          hasChanges: false,
        },
      };
    },
    props: {
      nested: {
        type: Boolean,
        default: false,
      },
    },
    computed: {
      ...mapGetters('USER', {
        userEntries: 'GET_ENTRIES',
      }),
      canViewUsers() {
        return $has(permissions.Users.View);
      },
      users() {
        return this.userEntries.filter(u => !u.isSuspended);
      },
      selectAllUsersIcon() {
        if (!this.selected.userIds || this.selected.userIds.length === 0) return 'mdi-checkbox-blank-outline';
        if (this.selected.userIds.length === this.users.length) return 'mdi-checkbox-marked';
        return 'mdi-minus-box';
      },
      createdDateAsText() {
        if (this.selected == null) return 'N/A';
        return moment(this.selected.createDate).format('YYYY/MM/DD, hh:mm A');
      },
      sideOutMinSize: {
        get() {
          return this.$store.getters['GET_SIDOUT_MINSIZE'];
        },
        set(val) {
          this.$store.commit('SET_SIDOUT_MINSIZE', val);
        },
      },
      sideOutCurrentSize: {
        get() {
          return this.$store.getters['GET_SIDOUT_CURRENTSIZE'];
        },
        set(val) {
          this.$store.commit('SET_SIDOUT_CURRENTSIZE', val);
        },
      },
    },
    methods: {
      OnResize() {
        let facilitySlideout = this.$refs.facilitySlideout;
        this.sideOutCurrentSize = facilitySlideout.resizeValue;
      },
      onSlideoutClosing() {
        this.$emit('close');
        this.closeUpdateSlideout();
      },
      updateConfirmed() {
        this.slideouts.isLoading = true;
        let toSend = structuredClone(this.selected);
        toSend.imageUrl = $stripQueryParams(toSend.imageUrl);
        this.$store
          .dispatch('FACILITY/UPDATE', toSend)
          .then(() => {
            var message = 'Facility updated successfully!';
            if (!toSend.id) {
              message = 'Facility added successfully!';
            }
            this.closeUpdateSlideout();
            this.$dialog.notify.success(message, {
              position: 'top-right',
              timeout: 3000,
            });
          })
          .catch((err) => {
            $handleError(err);
          })
          .finally(() => {
            this.slideouts.isLoading = false;
          });
      },
      getUserDisplayName(user) {
        if (user.displayName) return user.displayName;
        const first = user.firstName || '';
        const last = user.lastName || '';
        return (first + ' ' + last).trim() || user.email || 'User';
      },
      toggleSelectAllUsers() {
        if (this.selected.userIds && this.selected.userIds.length === this.users.length) {
          this.selected.userIds = [];
        } else {
          this.selected.userIds = this.users.map(u => u.id);
        }
      },
      removeUser(userId) {
        this.selected.userIds = this.selected.userIds.filter(id => id !== userId);
      },
      closeUpdateSlideout() {
        this.selected = { address: {}, userIds: [] };
        this.isFetching = false;
        this.cementFacility();
        this.slideouts.active = false;
        this.$refs.updateForm?.resetValidation();
      },
      open(id) {
        if (id != null) {
          this.FacilityId = id;
          this.fetchFacility();
          setTimeout(() => {
            this.$refs.updateForm?.resetValidation();
          });
        }
        this.$store.dispatch('CLIENT/GET_CLIENTS');
        if (this.canViewUsers && !this.userEntries.length){
          this.$store.dispatch('USER/GET_USERS');

        }
        this.loadFacilityGroups();
        this.slideouts.active = true;
      },
      fetchFacility() {
        this.isFetching = true;
        this.$store
          .dispatch('FACILITY/GET_BY_ID', {
            id: this.FacilityId,
            selectFacility: false,
          })
          .then((resp) => {
            log('EditFacility', 'Facility fetched', resp);
            this.isFetching = false;
            this.slideouts.isLoading = false;
            let data = structuredClone(resp.data);
            if (!data.address) data.address = {};
            if (!data.userIds) data.userIds = [];
            this.selected = data;
            this.cementFacility();
          })
          .catch((err) => {
            this.isFetching = false;
            this.closeUpdateSlideout();
            $handleError(err);
          });
      },
      cementFacility() {
        this.selectedCemented = structuredClone(this.selected);
        this.checkForChanges();
      },
      checkForChanges() {
        if (!this.slideouts.active) return;

        this.slideouts.hasChanges = !$isEqual(this.selected, this.selectedCemented);
      },
      loadFacilityGroups() {
        // Load facility groups for the dropdown
        this.$store.dispatch('GROUPS/GET').then(() => {
          this.facilityGroups = this.$store.getters['GROUPS/GET_ENTRIES'];
        }).catch((err) => {
          error('EditFacility', 'Failed to load facility groups', err);
          this.facilityGroups = [];
        });
      },
    },
    components: {
      SingleImageUploader,
      ClientSelector,
    },
    watch: {
      selected: {
        handler() {
          this.checkForChanges();
        },
        deep: true,
      },
      nested: {
        handler() {
          this.nested ? (this.sideOutCurrentSize = '80vw') : (this.sideOutCurrentSize = '35vw');
          this.nested ? (this.sideOutMinSize = '80vw') : (this.sideOutMinSize = '35vw');
        },
        deep: true,
      },
    },
  };
</script>
